import {reportToBugsnag} from '../../../../srcCommon/services/bugsnag/bugsnag';
import {ActionWithPayload} from '../../../helpers/reduxHelpers';
import {ContactModel} from '../../../models/api/contactModel';
import {ApiRecentConversations, RecentConversationsModel} from '../../../models/api/recentConversationsModel';
import {fetchApiAsync} from '../../../services/http/http';
import {ThunkAsyncAction} from '../../appThunk';

/*
 * Action types.
 */

export enum RecentCommunicationsActionsEnum {
  SET_RECENT_COMMUNICATIONS = '@@metadata/recentCommunications/SET_RECENT_COMMUNICATIONS',
  DISMISS_BANNER = '@@metadata/recentCommunications/DISMISS_BANNER',
  CLICK_BANNER = '@@metadata/recentCommunications/CLICK_BANNER',
  MARK_HIGHLIGHT_AS_VIEWED = '@@metadata/recentCommunications/MARK_HIGHLIGHT_AS_VIEWED',
}

/*
 * Actions.
 */

interface FetchCompletedPayload {
  contactUid: string;
  recentConversations: RecentConversationsModel;
  requestedAt: number;
}

interface SetRecentCommunicationsAction extends ActionWithPayload<FetchCompletedPayload> {
  type: RecentCommunicationsActionsEnum.SET_RECENT_COMMUNICATIONS;
}

interface BannerInteractionPayload {
  contactUid: string;
}

interface DismissRecentCommunicationsBannerAction extends ActionWithPayload<BannerInteractionPayload> {
  type: RecentCommunicationsActionsEnum.DISMISS_BANNER;
}

interface ClickBannerAction extends ActionWithPayload<BannerInteractionPayload> {
  type: RecentCommunicationsActionsEnum.CLICK_BANNER;
}

interface MarkHighlightAsViewedAction extends ActionWithPayload<BannerInteractionPayload> {
  type: RecentCommunicationsActionsEnum.MARK_HIGHLIGHT_AS_VIEWED;
}

export type RecentCommunicationsAction =
  | SetRecentCommunicationsAction
  | DismissRecentCommunicationsBannerAction
  | ClickBannerAction
  | MarkHighlightAsViewedAction;

/*
 * Action creators.
 */

function setRecentCommunicationsAction(
  contactUid: string,
  recentConversations: RecentConversationsModel,
  requestedAt: number,
): SetRecentCommunicationsAction {
  return {
    type: RecentCommunicationsActionsEnum.SET_RECENT_COMMUNICATIONS,
    payload: {
      contactUid,
      recentConversations,
      requestedAt,
    },
  };
}

export function fetchRecentConversationsAsyncAction(contact: ContactModel): ThunkAsyncAction<void> {
  return async (dispatch) => {
    try {
      const url = `${contact.url}/cards/recent_conversations`;
      const recentConversations = await fetchApiAsync(ApiRecentConversations, url);

      const requestedAt = new Date().valueOf();
      dispatch(setRecentCommunicationsAction(contact.uid, recentConversations, requestedAt));
    } catch (error) {
      reportToBugsnag(error);
      throw error;
    }
  };
}

export function dismissRecentCommunicationsBannerAction(
  contact: ContactModel,
): DismissRecentCommunicationsBannerAction {
  return {
    type: RecentCommunicationsActionsEnum.DISMISS_BANNER,
    payload: {
      contactUid: contact.uid,
    },
  };
}

export function clickRecentCommunicationsBannerAction(contact: ContactModel): ClickBannerAction {
  return {
    type: RecentCommunicationsActionsEnum.CLICK_BANNER,
    payload: {
      contactUid: contact.uid,
    },
  };
}

export function markHighlightAsViewedAction(contact: ContactModel): MarkHighlightAsViewedAction {
  return {
    type: RecentCommunicationsActionsEnum.MARK_HIGHLIGHT_AS_VIEWED,
    payload: {
      contactUid: contact.uid,
    },
  };
}
